import { useState, useEffect, useRef } from "react";
import { useVerificationPolling } from "@/hooks/useVerificationPolling";
import { useSearchParams } from "react-router-dom";
import AppDownloadGuide from "./AppDownloadGuide";
import Footer from "./Footer";
import QRAuthentication from "./QRAuthentication";
import VerifierResult from "./VerifierResult";
import MobileAuthPopup from "../modal/MobileAuthPopup";
import { isMobileDevice } from "@/utils/deviceDetection";

// Verifier View 컴포넌트
function VerifierView() {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "";

  const timerId = useRef<any>();

  const [qrUrl, setQrUrl] = useState<string>("");
  const [nonce, setNonce] = useState<string>("");
  const [timer, setTimer] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"auth" | "download">("auth");
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isMobilePopupOpen, setIsMobilePopupOpen] = useState<boolean>(false);

  // Polling Hook 사용
  const {
    verificationStatus,
    presentationResult,
    presentationRequestResult,
    stopPolling,
  } = useVerificationPolling({
    nonce,
    interval: 3000,
    maxAttempts: 100,
    enabled: !!nonce,
  });

  useEffect(() => {
    if (verificationStatus.status === "completed") {
      setIsSuccess(true);
    }
  }, [verificationStatus]);

  useEffect(() => {
    return () => {
      stopPolling();
      clearInterval(timerId.current);
    };
  }, []);

  useEffect(() => {
    if (timer === 0) {
      stopPolling();
      clearInterval(timerId.current);
    }
  }, [timer, timerId.current]);

  // 페이지 로드 시 Presentation Request 생성 및 nonce 가져오기
  const initializePresentationRequestQR = async (
    conditionKeyParam?: string
  ) => {
    try {
      setTimer(300); // 타이머를 5분(300초)으로 리셋
      const response = await fetch(
        `${
          import.meta.env.VITE_VERIFIER_SERVER_ENDPOINT
        }/api/v1/presentation_request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            conditionKey: conditionKeyParam,
          }),
        }
      );

      const result = await response.json();
      if (response.ok) {
        const { qr_url, nonce } = result?.data;
        setQrUrl(qr_url);
        setNonce(nonce);
        timerId.current = setInterval(() => {
          setTimer((prev) => prev - 1);
        }, 1000);
      }

      // nonce 값 추출 및 설정
    } catch (error) {
      console.error("Presentation Request 생성 실패:", error);
    }
  };

  // 컴포넌트 마운트 시 QR 생성
  useEffect(() => {
    if (type) {
      initializePresentationRequestQR(type);
    }
  }, [type]);

  // 모바일 기기에서 QR URL이 생성되면 팝업 자동 표시
  useEffect(() => {
    if (qrUrl && isMobileDevice()) {
      setIsMobilePopupOpen(true);
    }
  }, [qrUrl]);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* 헤더 */}
      {isSuccess && (
        <VerifierResult
          presentationResult={presentationResult}
          presentationRequestResult={presentationRequestResult}
        />
      )}
      {!isSuccess && (
        <>
          <header
            className="w-screen h-[88px] text-white bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('/images/header_bg_low.png')",
            }}
          >
            {" "}
            <div className="flex flex-col items-center pt-6 pb-4 px-4">
              <img
                className="inline"
                src="/images/mykeepin-logo.png"
                width={148}
                height={20}
                alt="mykeepin-logo"
              />
              <span className="mt-1 text-[17px]">똑똑한 관리의 시작!</span>
            </div>
          </header>

          {/* 탭 메뉴 */}
          <div className="flex justify-center bg-white">
            <div className="w-[360px] min-w-[360px] px-4">
              <div className="flex">
                <div
                  className="flex flex-auto h-[56px] justify-center"
                  tabIndex={0}
                  onClick={() => setActiveTab("auth")}
                >
                  <p
                    className={`flex items-center text-sm ${
                      activeTab === "auth"
                        ? "border-b-2 border-[#333] font-bold"
                        : ""
                    }`}
                  >
                    MYKEEPiN 인증
                  </p>
                </div>
                <div
                  className="flex flex-auto h-[56px] justify-center"
                  tabIndex={0}
                  onClick={() => setActiveTab("download")}
                >
                  <p
                    className={`flex items-center text-sm ${
                      activeTab === "download"
                        ? "border-b-2 border-[#333] font-bold"
                        : ""
                    }`}
                  >
                    MYKEEPiN 다운로드
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 메인 컨텐츠 */}
          {activeTab === "auth" && (
            <QRAuthentication
              qrUrl={qrUrl}
              seconds={timer}
              onRefresh={() => initializePresentationRequestQR(type)}
            />
          )}
          {activeTab === "download" && <AppDownloadGuide />}

          {/* 푸터 */}
          <Footer />
          <MobileAuthPopup
            onClose={() => setIsMobilePopupOpen(false)}
            onConfirm={() => {
              window.location.href = qrUrl;
            }}
            isOpen={isMobilePopupOpen}
          />
        </>
      )}
    </div>
  );
}

export default VerifierView;
